import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RpcError, StatusCode } from 'grpc-web';
import { API_URL, getCurrentLng, getHeaders, ApiMessage, toApiMessage, APIResponse, refreshSession, getSession, ACCOUNTANT_API_URL, APIRequest, TRADING_API_URL } from '../../../../../../app/Api';
import { formatDate, timestampToDate } from '../../../../../../app/Helpers';
import { TableState } from '../../../../../common/Table/TableSate';
import { CurrencyPairPriceLogEndpointClient } from '../../../../../../repository/Trading/Currency_pair_price_logServiceClientPb';
import { GetCurrencyPairPriceLogsRequest, GetCurrencyPairPriceLogsResponse, DeleteCurrencyPairPriceLogRequest, DeleteCurrencyPairPriceLogResponse } from '../../../../../../repository/Trading/currency_pair_price_log_pb';


export interface CurrencyPairPriceChangeLogPageState {
    set: TableState
    isChangeStateLoading: boolean,
    message?: ApiMessage,
    searchText: string | undefined,
    filters: any[],
    fromFilterOption: { value: string, text: string },
    toFilterOption: { value: string, text: string },
}

const initialState: CurrencyPairPriceChangeLogPageState = {

    set: { items: [], hasMore: true, isDescending: true, isFetching: false, numberOfResults: 10, selected: [], isFilteredSet: false },
    isChangeStateLoading: false,
    message: undefined,
    searchText: undefined,
    filters: [],
    fromFilterOption: { value: "", text: "" },
    toFilterOption: { value: "", text: "" },

}

const client = new CurrencyPairPriceLogEndpointClient(TRADING_API_URL, null, null);

export const getItems = createAsyncThunk<GetCurrencyPairPriceLogsResponse.AsObject, APIRequest<GetCurrencyPairPriceLogsRequest>, {

    rejectValue: ApiMessage
}>(
    'pages/trading/currencyPairDetails/currencyPairPriceLogs/fetch',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {
            try {

                var r = await client.getCurrencyPairPriceLogs(req.body, req.headers ?? {});
                return r.toObject();
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }
        }
        return await callReq();
    }

)






export const deleteItem = createAsyncThunk<APIResponse<DeleteCurrencyPairPriceLogResponse.AsObject>, APIRequest<DeleteCurrencyPairPriceLogRequest>, {

    rejectValue: ApiMessage
}>(
    'pages/trading/currencyPairDetails/currencyPairPriceLogs/delete',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {

            try {
                var r = await client.delete(req.body, req.headers ?? {});
                return { metadata: { id: req.body.getId() }, response: r.toObject() };
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }

        }
        return await callReq();
    }
)


const getFilters = (state: CurrencyPairPriceChangeLogPageState): any[] => {
    const selected = [];

    if (state.fromFilterOption.value && state.fromFilterOption.value != "") {
        selected.push({ key: 1, name: state.fromFilterOption.text });
    }
    if (state.toFilterOption.value && state.toFilterOption.value != "") {
        selected.push({ key: 2, name: state.toFilterOption.text });
    }

    return selected;
}

export const currencyPairPriceLogPageSlice = createSlice({
    name: 'pages/trading/currencyPairDetails/currencyPairPriceLogs',
    initialState,
    reducers: {
        dismissMessage: (state) => {
            state.message = undefined;
        },
        reset: (state) => {
            state.message = initialState.message;
            state.set = initialState.set;
            state.isChangeStateLoading = initialState.isChangeStateLoading;
            state.searchText = initialState.searchText;
            state.filters = initialState.filters;

        },
        resetSet: (state) => {
            state.set.items = [];
            state.set.hasMore = true;
        },


        setSort: (state, action: PayloadAction<boolean>) => {
            state.set.isDescending = action.payload;
        },
        setNumberOfResults: (state, action: PayloadAction<number>) => {
            state.set.numberOfResults = action.payload;
        },
        setSelectedItems: (state, action: PayloadAction<any[]>) => {
            state.set.selected = action.payload;
        },


        setSearchText: (state, action: PayloadAction<string | undefined>) => {
            state.searchText = action.payload;
        },
        setIsFilteredSet: (state, action: PayloadAction<boolean>) => {
            state.set.isFilteredSet = action.payload;
        },

        setFromFilterOption: (state, action: PayloadAction<{ value: string, text: string }>) => {
            state.fromFilterOption = action.payload;
            state.filters = getFilters(state);
        },
        setToFilterOption: (state, action: PayloadAction<{ value: string, text: string }>) => {
            state.toFilterOption = action.payload;
            state.filters = getFilters(state);
        },
        addItem: (state, action: PayloadAction<any>) => {
            if (!state.set.isFilteredSet) {
                if (!state.set.hasMore && !state.set.isDescending) {
                    state.set.items.push(action.payload);
                } else if (state.set.isDescending) {
                    state.set.items.unshift(action.payload);
                }
            }
        },
        updateItem: (state, action: PayloadAction<any>) => {
            let selected = state.set.selected;
            if (action?.payload) {
                if (selected.length > 0 && selected.at(0).id == action?.payload?.id) {
                    selected[0] = action?.payload
                }
                var u = state.set.items.findIndex(e => e.id == action?.payload?.id)
                if (u >= 0) {
                    state.set.items[u] = action?.payload
                }
            }

        },
        setMessage: (state, action: PayloadAction<ApiMessage>) => {
            state.message = action.payload;
        },
    },
    extraReducers: (builder) => {




        builder.addCase(getItems.fulfilled, (state, { payload }) => {
            if (payload) {
                var r = (payload?.success?.currencypairpricelogsList.map(val => {
                    // console.log(val)
                    let r = {
                        id: val.id,
                        buyPrice: val.buyprice?.value,
                        sellPrice: val.sellprice?.value ?? undefined,
                        tradingPrice: val?.tradingprice?.value ?? undefined,
                        currencyPairId: val.currencypair?.id?.value ?? "",
                        currencyPairName: val.currencypair?.name?.value ?? "",
                        baseCurrencyId: val.currencypair?.base?.id?.value ?? "",
                        baseCurrencyName: val.currencypair?.base?.name?.value ?? "",
                        baseCurrencyCode: val.currencypair?.base?.code?.value ?? "",
                        baseCurrencyDecimalPlaces: val.currencypair?.base?.decimalplaces?.value ?? undefined,
                        baseCurrencyForm: val.currencypair?.base?.form?.value ?? undefined,
                        baseCurrencySymbol: val.currencypair?.base?.symbol?.value ?? "",
                        quoteCurrencyId: val.currencypair?.quote?.id?.value ?? "",
                        quoteCurrencyName: val.currencypair?.quote?.name?.value ?? "",
                        quoteCurrencyCode: val.currencypair?.quote?.code?.value ?? "",
                        quoteCurrencyDecimalPlaces: val.currencypair?.quote?.decimalplaces?.value ?? undefined,
                        quoteCurrencyForm: val.currencypair?.quote?.form?.value ?? undefined,
                        quoteCurrencySymbol: val.currencypair?.quote?.symbol?.value ?? "",
                        trigger: val.trigger?.value ?? undefined,
                        volumeBase: val.volumebase?.value ?? undefined,
                        volumeQuote: val.volumequote?.value ?? undefined,
                        createdDate: formatDate(timestampToDate(val.createddate?.seconds, val.createddate?.nanos)),
                        addedBy: (val.createdby?.firstname?.value ?? "") + " " + (val.createdby?.lastname?.value ?? ""),
                        addedById: val.createdby?.id?.value,
                    }

                    //console.log(r)
                    return r;
                }
                ) as any[])
                //r = (r.concat(...Array(9).fill(r)));
                let l = [];
                if (state.set.items.length != 0) {
                    l = state.set.items.slice(0, state.set.items.length - 10)
                }
                l = l.concat(r);
                if (r.length < state.set.numberOfResults) {
                    state.set.hasMore = false;
                } else {
                    l = l.concat(...Array(10).fill(null));
                }

                state.set.items = l
                //console.log(state.items)

            } else {
                state.set.hasMore = false;
            }
            state.set.isFetching = false

        })
        builder.addCase(getItems.rejected, (state, action) => {
            state.set.isFetching = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(getItems.pending, (state, action) => {
            state.set.isFetching = true;
            state.message = undefined;
        })
        builder.addCase(deleteItem.fulfilled, (state, { payload }) => {
            state.isChangeStateLoading = false;
            if (payload) {
                state.message = toApiMessage(payload?.response.success?.message);
                let selected = state.set.selected;
                if (payload?.metadata) {
                    if (selected.length > 0 && selected.at(0).id == payload?.metadata.id) {
                        state.set.selected = []
                    }
                    var u = state.set.items.findIndex(e => e.id == payload?.metadata.id)
                    if (u >= 0) {
                        state.set.items.splice(u, 1);
                    }
                }
            }
        })
        builder.addCase(deleteItem.rejected, (state, action) => {
            state.isChangeStateLoading = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(deleteItem.pending, (state, action) => {
            state.isChangeStateLoading = true;
            state.message = undefined;
        })


    }
})

export const { reset, dismissMessage, setMessage, setSort, setFromFilterOption, setToFilterOption, setNumberOfResults, resetSet, setSelectedItems, setSearchText, setIsFilteredSet, addItem, updateItem, } = currencyPairPriceLogPageSlice.actions

export default currencyPairPriceLogPageSlice.reducer


